import { Grid, Paper } from '@mui/material';
import dayjs from 'dayjs';

import { useEffect, useState } from 'react';
import { ReactHookForm } from '../../../components/hook-form/react-hook-form.component';
import { SiTrackGrid } from '../../../components/si-track-grid/si-track-grid';
import { SiTrackPage } from '../../../components/si-track-page';
import { useReactHookForm } from '../../../hooks/useReactHookForm';
import { useSaveData } from '../../../hooks/useSaveData';

import { columns } from './grid-columns';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useSnackbar } from '../../../hooks/use-snackbar.hook';
import EmailLogFilters from './email-log-filters';
import { emailLogApiKeyStore } from '../../../api/masters/email-log-api-key-store';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

const EmailLogPage = () => {
  const { showSnackbar } = useSnackbar();
  const today = dayjs().format('YYYY-MM-DD');
  const lastWeek = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
  const form = useReactHookForm({
    defaultValues: {
      office: { officeID: '', officeName: '' },
      project: { projectID: '', projectName: '' },
      unit: { unitID: '', unitName: '' },
      fromDate: lastWeek,
      toDate: today,
    },
  });

  const [currSearch, setCurrSearch] = useState<any>(null);
  const [currFilters, setCurrFilters] = useState<any>(null);
  const [currGroups, setCurrGroups] = useState<any>(null);
  const [currPageNumber, setCurrPageNumber] = useState<any>(1);
  const [currSize, setCurrSize] = useState<any>(50);
  const [currSkip, setCurrSkip] = useState<any>(0);
  const [currSort, setCurrSort] = useState<any>([]);
  const [filterData, setFilterData] = useState<any>();
  const [emailData, setEmailData] = useState<any>();
  const [totalRecords, setTotalRecords] = useState<any>(0);

  const {
    isLoading: isGenerating,
    data,
    mutateAsync,
  } = useSaveData<any>(emailLogApiKeyStore.generateEmailLogWithPagination());

  const { isLoading: isExcelLoading, mutateAsync: mutateExcelAsync } = useSaveData<any>(emailLogApiKeyStore.exportEmailExcel());

  useEffect(() => {
    if (data) {
      const result: any = data;
      setEmailData(result?.data);
      setTotalRecords(result?.totalRecords);
    }
  }, [data])

  const handleSubmit = async (filtersData: any) => {
    const filterDataValue = {
      pageNumber: 1,
      pageSize: 50,
      sortExp: null,
      sortDir: null,
      searchText: null,
      filterJson: null,
      groupJson: null,
      startDate: filtersData?.fromDate ? dayjs(filtersData?.fromDate).format('YYYY-MM-DD') : null,
      endDate: filtersData?.toDate ? dayjs(filtersData?.toDate).format('YYYY-MM-DD') : null,
      projectID: Number(filtersData?.project?.projectID),
      officeID: Number(filtersData?.office?.officeID),
      customerID: Number(filtersData?.customer?.customerID),
      unitID: Number(filtersData?.unit?.unitID),
      workOrderID: 0,
    };
    setCurrSize(50);
    setCurrSkip(0);
    setCurrSort([]);
    setCurrFilters(null);
    setFilterData(filterDataValue);
    await mutateAsync({ data: filterDataValue });
  };

  const handlePageChange = async (pageNumber: any, pageSize: any, pageSkip: any) => {
    const sorting: any = currSort?.length > 0 && currSort[0];
    setFilterData((prev: any) => ({
      ...prev,
      pageNumber,
      pageSize,
    }));

    setCurrPageNumber(pageNumber);
    setCurrSize(pageSize);
    setCurrSkip(pageSkip);

    await mutateAsync({
      data: {
        ...filterData,
        pageNumber,
        pageSize,
        sortExp: sorting?.field ?? null,
        sortDir: sorting?.dir?.toUpperCase() ?? null,
        filterJson: currFilters ? JSON.stringify(currFilters) : null,
        groupJson: currGroups ? JSON.stringify(currGroups) : null
      },
    });
  }

  const handleColumnSort = async (columnSort: any[]) => {
    const sorting: any = columnSort?.length > 0 && columnSort[0];
    setCurrSort(columnSort);
    await mutateAsync({
      data: {
        ...filterData,
        pageNumber: currPageNumber,
        pageSize: currSize,
        sortExp: sorting?.field ?? null,
        sortDir: sorting?.dir?.toUpperCase() ?? null,
        filterJson: currFilters ? JSON.stringify(currFilters) : null,
        groupJson: currGroups ? JSON.stringify(currGroups) : null
      },
    });
  }

  const handleColumnFilter = async (columnFilter: any) => {
    const sorting: any = currSort?.length > 0 && currSort[0];
    setCurrFilters(columnFilter);
    setCurrPageNumber(1);
    setCurrSkip(0);
    await mutateAsync({
      data: {
        ...filterData,
        pageNumber: 1,
        pageSize: currSize,
        sortExp: sorting?.field ?? null,
        sortDir: sorting?.dir?.toUpperCase() ?? null,
        filterJson: columnFilter ? JSON.stringify(columnFilter) : null,
        groupJson: currGroups ? JSON.stringify(currGroups) : null
      },
    });
  }

  const handleColumnGroup = async (columnGroup: any) => {
    const sorting: any = currSort?.length > 0 && currSort[0];
    setCurrGroups(columnGroup);
    setCurrPageNumber(1);
    setCurrSkip(0);
    await mutateAsync({
      data: {
        ...filterData,
        pageNumber: 1,
        pageSize: currSize,
        sortExp: sorting?.field ?? null,
        sortDir: sorting?.dir?.toUpperCase() ?? null,
        filterJson: currFilters ? JSON.stringify(currFilters) : null,
        groupJson: columnGroup ? JSON.stringify(columnGroup) : null
        //isBasicFilter: null
      },
    });
  }

  const handleSearchData = async (searchText: any) => {
    const sorting: any = currSort?.length > 0 && currSort[0];
    setCurrSearch(searchText ?? null);
    if (searchText) {
      await mutateAsync({
        data: {
          ...filterData,
          pageNumber: currPageNumber,
          pageSize: currSize,
          sortExp: sorting?.field ?? null,
          sortDir: sorting?.dir?.toUpperCase() ?? null,
          searchText: searchText ?? null,
          filterJson: currFilters ? JSON.stringify(currFilters) : null,
          groupJson: currGroups ? JSON.stringify(currGroups) : null
        },
      });
    }
  }

  const handleExportExcel = async () => {

    // Omit unwanted keys from selectedFilters
    const { pageNumber, pageSize, sortExp, sortDir, searchText, filterJson, ...exportFilters } = filterData || {};
    const excelPayload = {
      ...exportFilters
    };
    await mutateExcelAsync({ data: excelPayload });
    showSnackbar(`Your export is in progress. You will receive an email once it is ready.`, 'success');
  }

  const handleSubmitClick = () => {
    form.handleSubmit(handleSubmit)();
  };

  return (
    <SiTrackPage title="Email Log">
      <ReactHookForm formName="EMAIL_LOG_FORM" onSubmit={() => { }} formContextProps={form}>
        <Grid container spacing={1}>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <EmailLogFilters onSubmit={handleSubmitClick} />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGrid
                fileTitle='email_log'
                columns={columns}
                dataItemKey="notificationID"
                dataItems={emailData}
                isBusy={isGenerating || isExcelLoading}
                currentPageSize={currSize}
                currentSkip={currSkip}
                currentSort={currSort}
                currentFilter={currFilters}
                currentGroup={currGroups}
                isServerSearching={true}
                // isExportServerExcel={true}
                hideExportExcel={true}
                totalRecords={totalRecords}
                onPageChange={handlePageChange}
                onSortChange={handleColumnSort}
                onFilterChange={handleColumnFilter}
                onGroupChange={handleColumnGroup}
                onSearchChange={handleSearchData}
                // onExportExcel={handleExportExcel}
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
    </SiTrackPage>
  );
};

export default EmailLogPage;
